import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { tokenManager } from '../lib/api';
import {
  authApi,
  User,
  AuthTokens,
  Membership,
  LoginRequest,
  SignupUsernameRequest,
  SignupEmailRequest,
  RestorePendingResponse,
  extractApiError,
} from '../lib/auth';
// 저장된 아이디 키 (로그인 페이지에서도 사용)
export const SAVED_ID_KEY = 'safetyhub_saved_id';

// 빙의 모드 정보
interface ImpersonationInfo {
  adminId: string;
  adminEmail: string;
  targetUserId: string;
  targetUserName: string;
}

// JWT 페이로드 디코딩 (빙의 상태 복원용)
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

interface AuthState {
  // 상태
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // 현재 선택된 회사 (Phase H-8e: companyId source = m.companyId)
  currentCompanyId: string | null;
  currentMembership: Membership | null;

  // 빙의 모드
  impersonation: ImpersonationInfo | null;

  // 탈퇴 유예 계정 복원 대기 상태 (휘발성 — persist 제외)
  restorePending: { daysLeft: number; restoreToken: string } | null;

  // 액션
  login: (data: LoginRequest) => Promise<
    | { accountRestored: boolean; organizationRestored: boolean }
    | RestorePendingResponse
  >;
  restore: (restoreToken: string) => Promise<void>;
  clearRestorePending: () => void;
  signupWithUsername: (data: SignupUsernameRequest) => Promise<void>;
  signupWithEmail: (data: SignupEmailRequest) => Promise<void>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
  refreshSession: (preferredCompanyId?: string) => Promise<void>;
  setCurrentOrganization: (companyId: string) => void;
  clearError: () => void;
  initialize: () => Promise<void>;
  setImpersonation: (info: ImpersonationInfo) => void;
  endImpersonation: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // 초기 상태
      user: null,
      isAuthenticated: false,
      isLoading: true, // 초기 로딩 상태
      error: null,
      currentCompanyId: null,
      currentMembership: null,
      impersonation: null,
      restorePending: null,

      // 로그인 (계정 복원 여부, 조직 복원 여부 반환)
      // 탈퇴(SELF) 유예 기간 내 계정은 RestorePendingResponse 그대로 반환 (토큰/유저 미저장)
      login: async (data: LoginRequest): Promise<
        | { accountRestored: boolean; organizationRestored: boolean }
        | RestorePendingResponse
      > => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.login(data);

          // 복원 대기 상태 — store에 저장하고 호출자에게도 반환 (App 레벨 모달이 store 구독)
          if ('restorePending' in response && response.restorePending) {
            set({
              isLoading: false,
              restorePending: { daysLeft: response.daysLeft, restoreToken: response.restoreToken },
            });
            return response;
          }

          const normalResponse = response as { user: User; tokens: AuthTokens; accountRestored?: boolean };

          tokenManager.setTokens(normalResponse.tokens.accessToken, normalResponse.tokens.refreshToken);

          // 마지막으로 선택된 회사 복원 시도
          const savedCompanyId = get().currentCompanyId;
          let selectedMembership = savedCompanyId
            ? normalResponse.user.memberships.find((m) => m.companyId === savedCompanyId)
            : null;
          let organizationRestored = !!selectedMembership;

          // 저장된 조직이 삭제된 경우 첫 번째 조직으로 전환
          if (selectedMembership?.company.deletedAt) {
            selectedMembership = normalResponse.user.memberships[0] || null;
            organizationRestored = false;
          }

          // 저장된 회사가 없거나 유효하지 않으면 첫 번째 조직 선택
          if (!selectedMembership) {
            selectedMembership = normalResponse.user.memberships[0] || null;
            // savedCompanyId가 없었던 경우는 복원 실패가 아님 (첫 로그인 또는 정상 상태)
            if (savedCompanyId) {
              organizationRestored = false;
            }
          }

          set({
            user: normalResponse.user,
            isAuthenticated: true,
            isLoading: false,
            currentCompanyId: selectedMembership?.companyId || null,
            currentMembership: selectedMembership,
          });

          return {
            accountRestored: normalResponse.accountRestored ?? false,
            organizationRestored,
          };
        } catch (error) {
          const apiError = extractApiError(error);
          set({
            isLoading: false,
            error: apiError.message,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      // 복원 확정 — restoreToken으로 /auth/restore 호출 후 정식 로그인 처리
      restore: async (restoreToken: string): Promise<void> => {
        // isLoading 토글 금지 — App의 {isLoading ? 로딩 : Routes} 구조가 Routes를 언마운트하면
        // 진행 중인 restore 요청이 끊긴다(ERR_FAILED). 모달은 자체 로컬 loading을 사용.
        set({ error: null });
        try {
          const response = await authApi.restore(restoreToken);
          tokenManager.setTokens(response.tokens.accessToken, response.tokens.refreshToken);

          const savedCompanyId = get().currentCompanyId;
          let selectedMembership = savedCompanyId
            ? response.user.memberships.find((m) => m.companyId === savedCompanyId)
            : null;

          if (selectedMembership?.company.deletedAt) {
            selectedMembership = response.user.memberships[0] || null;
          }

          if (!selectedMembership) {
            selectedMembership = response.user.memberships[0] || null;
          }

          set({
            user: response.user,
            isAuthenticated: true,
            currentCompanyId: selectedMembership?.companyId || null,
            currentMembership: selectedMembership,
            restorePending: null,
          });
        } catch (error) {
          const apiError = extractApiError(error);
          // isLoading 토글 금지 — restore()는 isLoading을 건드리지 않음(주석 참조).
          // 에러 상태만 기록하고 throw → RestoreConfirmModal catch에서 UI 표시.
          set({ error: apiError.message });
          throw error;
        }
      },

      // 복원 대기 상태 초기화 (복원 취소 시)
      clearRestorePending: () => set({ restorePending: null }),

      // USERNAME 타입 회원가입
      signupWithUsername: async (data: SignupUsernameRequest) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.signupWithUsername(data);
          tokenManager.setTokens(response.tokens.accessToken, response.tokens.refreshToken);

          // 기본 조직 선택 (개인 조직)
          const firstMembership = response.user.memberships[0] || null;

          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
            currentCompanyId: firstMembership?.companyId || null,
            currentMembership: firstMembership,
          });
        } catch (error) {
          const apiError = extractApiError(error);
          set({
            isLoading: false,
            error: apiError.message,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      // EMAIL 타입 회원가입
      signupWithEmail: async (data: SignupEmailRequest) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.signupWithEmail(data);
          tokenManager.setTokens(response.tokens.accessToken, response.tokens.refreshToken);

          // 기본 조직 선택 (개인 조직)
          const firstMembership = response.user.memberships[0] || null;

          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
            currentCompanyId: firstMembership?.companyId || null,
            currentMembership: firstMembership,
          });
        } catch (error) {
          const apiError = extractApiError(error);
          set({
            isLoading: false,
            error: apiError.message,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      // 로그아웃
      logout: async () => {
        set({ isLoading: true });
        try {
          await authApi.logout();
        } finally {
          tokenManager.clearTokens();
          // 저장된 아이디 삭제
          localStorage.removeItem(SAVED_ID_KEY);
          // currentCompanyId는 유지 (다음 로그인 시 복원용)
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            currentMembership: null,
          });
        }
      },

      // 사용자 정보 갱신
      fetchUser: async () => {
        // 빙의 토큰 또는 일반 토큰 확인
        const token = tokenManager.getCurrentToken();
        if (!token) {
          set({ isAuthenticated: false, user: null, isLoading: false });
          return;
        }

        set({ isLoading: true });
        try {
          const user = await authApi.getMe();
          const state = get();

          // 현재 회사가 유효한지 확인
          let currentMembership = user.memberships.find(
            (m) => m.companyId === state.currentCompanyId
          );

          // 현재 조직이 삭제된 경우 첫 번째 조직으로 전환
          if (currentMembership?.company.deletedAt) {
            currentMembership = user.memberships[0] || null;
          }

          // 현재 조직이 없으면 첫 번째 조직 선택
          if (!currentMembership && user.memberships.length > 0) {
            currentMembership = user.memberships[0];
          }

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            currentCompanyId: currentMembership?.companyId || null,
            currentMembership: currentMembership || null,
          });
        } catch (error) {
          // 네트워크 에러(서버 재시작 등)인 경우 토큰 및 인증 상태 유지
          const isNetworkError =
            error instanceof Error &&
            (('code' in error && error.code === 'ERR_NETWORK') ||
              (error as { response?: unknown }).response === undefined);

          if (isNetworkError) {
            console.log('[auth] Network error during fetchUser, keeping auth state');
            set({ isLoading: false });
            return;
          }

          // 401 에러 등 인증 에러 - API 인터셉터에서 토큰 처리됨
          // persist된 상태가 있으면 유지 (서버 재시작 중일 수 있음)
          const currentState = get();
          if (currentState.isAuthenticated && currentState.user) {
            console.log('[auth] Auth error during fetchUser, but keeping persisted state');
            set({ isLoading: false });
            return;
          }

          // persist된 상태가 없으면 로그아웃 처리
          tokenManager.clearTokens();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            currentMembership: null,
          });
        }
      },

      /**
       * 소속을 다시 읽되 **화면을 유지**하는 세션 갱신.
       *
       * fetchUser와 다른 점 두 가지:
       *  1) isLoading을 토글하지 않는다 — App의 `{isLoading ? 로딩 : Routes}` 구조에서 토글은
       *     Routes를 통째로 언마운트해, 진행 중 화면의 로컬 상태(예: 조직 통합 수락 완료 요약)를
       *     날리고 재진입시킨다(restore()가 같은 이유로 토글을 금지한 선례).
       *  2) 옮겨갈 조직을 호출자가 지정한다 — 내 조직이 사라지는 흐름(통합 수락)에서 fetchUser의
       *     `memberships[0]` 폴백은 정본이 아니라 아무 소속(개인 워크스페이스)으로 떨어진다.
       *
       * 갱신 실패는 삼킨다 — 이 호출은 이미 성공한 작업의 뒷정리라, 실패가 완료 화면을
       * 에러로 되돌리면 안 된다.
       */
      refreshSession: async (preferredCompanyId?: string) => {
        const token = tokenManager.getCurrentToken();
        if (!token) return;

        try {
          const user = await authApi.getMe();
          const pick = (companyId?: string | null) =>
            companyId
              ? user.memberships.find(
                  (m) => m.companyId === companyId && !m.company.deletedAt,
                )
              : undefined;

          // 지정 조직 → 머물던 조직(살아 있다면) → 남은 첫 **살아 있는** 소속
          // (마지막 폴백까지 deletedAt을 걸러야 한다 — 통합으로 방금 사라진 조직이 목록 앞에
          //  그대로 남아 있어, 거르지 않으면 소멸한 조직으로 되돌아간다)
          const membership =
            pick(preferredCompanyId) ??
            pick(get().currentCompanyId) ??
            user.memberships.find((m) => !m.company.deletedAt) ??
            null;

          set({
            user,
            isAuthenticated: true,
            currentCompanyId: membership?.companyId || null,
            currentMembership: membership,
          });
        } catch {
          // 세션 갱신 실패 — 다음 fetchUser/재진입에서 자연히 맞춰진다.
        }
      },

      // 조직 변경 (companyId로 멤버십 찾기 — Phase H-8e)
      setCurrentOrganization: (companyId: string) => {
        const { user } = get();
        if (!user) return;

        const membership = user.memberships.find(
          (m) => m.companyId === companyId
        );

        if (membership) {
          set({
            currentCompanyId: companyId,
            currentMembership: membership,
          });
        }
      },

      // 에러 초기화
      clearError: () => set({ error: null }),

      // 앱 초기화 (토큰 확인 후 사용자 정보 로드)
      // 네트워크 에러 재시도는 API 인터셉터에서 처리
      initialize: async () => {
        // 빙의 토큰 우선 확인
        const impToken = tokenManager.getImpersonationToken();
        if (impToken) {
          // 빙의 토큰이 있으면 빙의 상태 복원
          const payload = decodeJwtPayload(impToken);
          if (payload && payload.type === 'impersonation') {
            await get().fetchUser();
            const user = get().user;
            if (user) {
              set({
                impersonation: {
                  adminId: payload.adminId as string,
                  adminEmail: payload.adminEmail as string,
                  targetUserId: payload.sub as string,
                  targetUserName: user.name || '알 수 없음',
                },
              });
            }
            return;
          } else {
            // 잘못된 토큰이면 삭제
            tokenManager.clearImpersonationToken();
          }
        }

        // 일반 로그인 토큰 확인
        const token = tokenManager.getAccessToken();
        if (token) {
          await get().fetchUser();
        } else {
          set({ isAuthenticated: false, isLoading: false });
        }
      },

      // 빙의 모드 설정
      setImpersonation: (info: ImpersonationInfo) => {
        set({ impersonation: info });
      },

      // 빙의 모드 종료 (상태만 초기화, 토큰은 ImpersonationBanner에서 삭제)
      endImpersonation: () => {
        set({
          user: null,
          isAuthenticated: false,
          impersonation: null,
          currentMembership: null,
        });
      },
    }),
    {
      name: 'auth-storage',
      // Admin과 동일하게 인증 관련 상태 전체 persist
      // 서버 재시작 시에도 인증 상태 유지
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        currentCompanyId: state.currentCompanyId,
        currentMembership: state.currentMembership,
        // impersonation은 sessionStorage에서 관리하므로 제외
      }),
      // Phase H-6: localStorage migration — 기존 currentOrganizationId → currentCompanyId
      // currentMembership이 persist되므로 rehydration 후 fetchUser에서 companyId 재설정됨
      version: 1,
      migrate: (persisted: unknown, version: number) => {
        if (version === 0) {
          // v0 → v1: currentOrganizationId 필드 제거, currentCompanyId는 fetchUser에서 채워짐
          const old = persisted as Record<string, unknown>;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { currentOrganizationId: _legacyOrgId, ...rest } = old;
          return { ...rest, currentCompanyId: null };
        }
        return persisted as Record<string, unknown>;
      },
    }
  )
);

// 선택자 훅
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);
export const useCurrentOrganization = () => useAuthStore((state) => state.currentMembership);
export const useImpersonation = () => useAuthStore((state) => state.impersonation);
